import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { SigninConfiguration } from '@app/shared/models/signinConfiguration';
import { Observable, Subscription, first } from 'rxjs';
import { Constant } from 'src/constant';
import { Identity } from '@app/shared/models/identity';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ForgotPasswordActions, SigninActions } from '../store/actions';
import * as fromSignin from '../store/reducers';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SigninComponent implements OnInit, OnDestroy {

  identity$: Observable<Identity>;
  mfaRequired$: Observable<Boolean>;
  redirectSsoProcessing$: Observable<Boolean>;

  configuration$: Observable<SigninConfiguration>;
  configurationLoading$: Observable<Boolean>;
  configurationError$: Observable<any>;

  signinWithPasswordLoading$: Observable<Boolean>;
  signinWithPasswordError$: Observable<any>;

  signinForm: FormGroup;
  submittedEmail = false;
  submittedPassword = false;
  subscriptions: Subscription[] = [];

  signinStep = 'sso';

  SSO = Constant.signinMode.sso;
  STANDARD = Constant.signinMode.standard;

  redirectionIsChecked = false;

  constructor(
    private store$: Store<fromRoot.State & fromSignin.State>,
    private location: Location,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.redirectSsoProcessing$ = this.store$.pipe(select(fromSignin.getRedirectSsoProcessing));

    // Check automatic redirect sso
    this.route.queryParams.subscribe(({ redirectSso }) => {
      if (redirectSso) {
        this.store$.dispatch(SigninActions.automaticRedirectSso({ encodedSsoInformations: redirectSso }));
      } else {
        this.redirectionIsChecked = true;
      }
    });

    this.mfaRequired$ = this.store$.pipe(select(fromSignin.getMfaRequired));
    this.configuration$ = this.store$.pipe(select(fromSignin.getSigninConfiguration));
    this.configurationLoading$ = this.store$.pipe(select(fromSignin.getSigninConfigurationLoading));
    this.configurationError$ = this.store$.pipe(select(fromSignin.getSigninConfigurationError));

    this.signinWithPasswordLoading$ = this.store$.pipe(select(fromSignin.getSigninWithPasswordLoading));
    this.signinWithPasswordError$ = this.store$.pipe(select(fromSignin.getSigninWithPasswordError));

    this.store$.dispatch(ForgotPasswordActions.resetForgot());

    this.subscriptions.push(
      this.configuration$.subscribe(configuration => {
        if (!!configuration && configuration.type === Constant.signinMode.standard) {
          this.signinStep = 'password';
          this.signinForm.controls.password.setValidators(Validators.required);
          this.signinForm.controls.password.updateValueAndValidity();
        }
      }),
      this.signinWithPasswordError$.subscribe(error => {
        if (!!error) {
          this.signinForm.controls.password.reset();
        }
      }),
      this.mfaRequired$.pipe(first(required => !!required)).subscribe(mfaRequired => {
        if (mfaRequired) {
          this.router.navigate(['/signin/mfa']);
        }
      }),
    )
  }

  ngOnInit() {
    this.store$.dispatch(SigninActions.resetSignin());
    this.buildForm();
    this.redirectLangUrl();
  }

  redirectLangUrl() {
    const userLang = this.translateService.currentLang;

    if (userLang === Constant.languageFallback) {
      return;
    }

    this.location.go(userLang + '/signin');
  }

  onSubmit() {
    if (this.signinStep === 'password') {
      this.signinWithPassword();
    } else {
      this.loadSigninConfiguration();
    }
  }

  loadSigninConfiguration() {
    this.submittedEmail = true;
    if (this.signinForm.invalid) {
      return;
    }
    this.store$.dispatch(SigninActions.loadSigninConfiguration({ username: this.signinForm.value.username }));
  }

  signinWithPassword() {
    this.submittedPassword = true;
    if (this.signinForm.invalid) {
      return;
    }
    this.store$.dispatch(SigninActions.signinWithPassword({ username: this.signinForm.value.username, password: this.signinForm.value.password }));
  }

  get f() { return this.signinForm.controls; }

  buildForm() {
    this.signinForm = new FormGroup({
      username: new FormControl('', [Validators.email, Validators.required]),
      password: new FormControl(''),
    });
  }

  backToEmail() {
    this.signinStep = 'email';
    this.submittedEmail = false;
    this.submittedPassword = false;
    this.signinForm.controls.password.reset();
    this.signinForm.controls.password.setValidators(null);
    this.signinForm.controls.password.setErrors(null);
    this.store$.dispatch(SigninActions.resetSignin());
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.submittedEmail = false;
    this.submittedPassword = false;
    this.signinForm.reset();
    this.store$.dispatch(SigninActions.resetSignin());
  }
}
